import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import { HxAlert, HxButton, HxInput } from "../components/hx";
// HxField's label has no htmlFor, so the template's FieldLabel ties "Code" to
// the field here. Record: docs/testing/ui-migration/088-totp-challenge.md.
import { Field, FieldGroup, FieldLabel } from "../components/ui/field";
import { ApiError, opsFetch } from "../api";
import { resumeSsoContinue } from "../sso-continue";

interface Props {
  onAuthChanged: () => Promise<void> | void;
}

export default function TotpChallenge({ onAuthChanged }: Props) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await opsFetch<{ ok: true }>("/ops/auth/totp", {
        method: "POST",
        json: { code: code.trim() },
      });
      await onAuthChanged();
      // Back to the product that sent the user to sign in, if one did.
      if (!resumeSsoContinue()) navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "rate_limited") {
          setError("Too many attempts. Try again in a few minutes.");
        } else if (err.code === "session_expired") {
          setError("Login window expired. Sign in again.");
          navigate("/login", { replace: true });
        } else if (err.code === "not_implemented") {
          setError("TOTP not wired up yet (Phase 0 step 5 pending).");
        } else {
          setError("Invalid code. Try again.");
        }
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Enter your 6-digit code"
      subtitle="Open your authenticator app, or use a one-time recovery code if you've lost access."
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <FieldGroup className="gap-4">
          <Field className="gap-1.5">
            <FieldLabel htmlFor="code">Code</FieldLabel>
            <HxInput
              id="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9a-zA-Z-]{6,}"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
              className="w-full font-mono tracking-wider"
            />
          </Field>
        </FieldGroup>
        <HxButton variant="primary" type="submit" disabled={busy || code.length < 6} className="w-full">
          {busy ? "Verifying…" : "Verify"}
        </HxButton>
        {error && (
          <HxAlert className="mb-0" aria-live="polite">
            {error}
          </HxAlert>
        )}
      </form>
    </AuthShell>
  );
}
